import { styled, TableCell, TableRow, Typography } from '@mui/material';
import { VyTooltip } from '../../../../../../components/new-design/VyTooltip';

import type { SubscriptionProduct } from '@vy/services';
import { isSubscriptionProduct, SubscriptionPriceEstimator } from '@vy/services';
import { getDiscountConfiguration } from '@vy/services';
import type { Currency, FixedProductInput, PlanBillingData, PlanInputFormFixedProduct } from '@vy/types';
import { PRICING_MODELS } from '@vy/types';
import { formatNumber, isNotNullOrUndefined, isNullOrUndefined, isNumber } from '@vy/utils';
import { get, isNaN } from 'lodash';
import React, { useRef } from 'react';
import type { FieldPath } from 'react-hook-form';
import { Price } from '../../../../../../components/design/Price';
import { getFixedProductQuantityFieldPath } from '../../FixedChargesEditTable/fixed-charges-edit-table-utils';
import { buildValidBillingData, getProductInputEstimation } from '../utils/live-preview-estimation';

const StyledPrice = styled(Price)({
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '22px',
});

type FixedProductSectionTableRowProps = {
  fixedProduct: PlanInputFormFixedProduct;
  billingData: Partial<PlanBillingData>;
  currency: Currency;
};

export const FixedProductSectionTableRow: React.FC<FixedProductSectionTableRowProps> = ({
  fixedProduct,
  billingData,
  currency,
}) => {
  const quantityFieldPath = getFixedProductQuantityFieldPath(fixedProduct.variants[0].pricing.pricingModel);
  const quantityFieldValue = isNullOrUndefined(quantityFieldPath)
    ? undefined
    : get(fixedProduct, quantityFieldPath);
  const quantityValue = isNaN(quantityFieldValue) ? undefined : quantityFieldValue;

  const isCommission = fixedProduct.variants[0].pricing.pricingModel === PRICING_MODELS.Commission;
  const amountFieldPath: FieldPath<FixedProductInput> = isCommission
    ? 'variants.0.pricing.priceConfiguration.percentage'
    : 'variants.0.pricing.priceConfiguration.price';
  const amountFieldValue = get(fixedProduct, amountFieldPath);
  const amountValue = isNaN(amountFieldValue) ? undefined : amountFieldValue;

  const price = getProductInputEstimation(fixedProduct, billingData);
  const discountConfiguration = getDiscountConfiguration(fixedProduct);
  const isDiscountProduct = isNotNullOrUndefined(discountConfiguration);
  const productNameRef = useRef<HTMLElement>(null);

  const shouldHideAmountValue = shouldNotDisplayAmountValue(
    price,
    amountValue,
    isDiscountProduct,
    isCommission,
    fixedProduct,
  );

  return (
    <TableRow
      sx={{
        '&:last-child td, &:last-child th': {
          borderBottom: 0,
        },
        'td, th': {
          position: 'relative',
        },
      }}
    >
      <TableCell component="th" scope="row" className="px-0 py-2">
        <VyTooltip tooltipContent={fixedProduct.variants[0].name} placement="top" arrow>
          <Typography
            ref={productNameRef}
            className="truncate"
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "22px",
              color: "text.secondary"
            }}>
            {fixedProduct.variants[0].name}
          </Typography>
        </VyTooltip>
      </TableCell>
      <TableCell className="px-0 py-2" align="center">
        {!isNumber(quantityValue) ? (
          '-'
        ) : (
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "22px"
            }}>
            {formatNumber(quantityValue ?? 0)}
          </Typography>
        )}
      </TableCell>
      <TableCell className="px-0 py-2" align="center">
        {shouldHideAmountValue ? (
          '-'
        ) : (
          <StyledPrice currency={currency} align="center" className="w-full">
            {amountValue}
          </StyledPrice>
        )}
      </TableCell>
      {isCommission && (
        <TableCell className="px-0 py-2" align="right">
          <Typography
            className="first-letter:mr-1"
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "22px"
            }}>
            %{/*{formatNumber(amountValue ?? 0)}*/}
          </Typography>
        </TableCell>
      )}
      {!isCommission && (
        <TableCell className="px-0 py-2" align="right">
          <StyledPrice currency={currency} align="right" className="w-full">
            {getPrice(fixedProduct, isDiscountProduct, amountValue, price, billingData)}
          </StyledPrice>
        </TableCell>
      )}
    </TableRow>
  );
};

function shouldNotDisplayAmountValue(
  price: number,
  amountValue: number | undefined,
  isDiscountProduct: boolean,
  isCommission: boolean,
  fixedProduct: PlanInputFormFixedProduct,
) {
  const isAmountEqualToPrice = price === amountValue;
  const isDiscountWithoutSubscription = isDiscountProduct && !isSubscriptionProduct(fixedProduct);
  const isCommissionProduct = isCommission;

  return isAmountEqualToPrice || isDiscountWithoutSubscription || isCommissionProduct;
}

function getPrice(
  fixedProduct: PlanInputFormFixedProduct,
  isDiscountProduct: boolean,
  amountValue: number | undefined,
  price: number,
  billingData: Partial<PlanBillingData>,
) {
  if (isSubscriptionProduct(fixedProduct) && isDiscountProduct) {
    return getSubscriptionProductPriceBeforeDiscount(fixedProduct, billingData);
  }

  if (isDiscountProduct) {
    return amountValue;
  }

  return price;
}

function getSubscriptionProductPriceBeforeDiscount(
  product: SubscriptionProduct,
  billingData: Partial<PlanBillingData>,
) {
  const data = buildValidBillingData(billingData);

  return new SubscriptionPriceEstimator().getPriceWithoutDiscount(product, data);
}
